import React from "react";
import { Box, Tooltip } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { WorkOrderAssignee } from "../../models/models";

// Deterministic avatar colors based on userId hash
const AVATAR_COLORS = [
  "#3498DB", "#E74C3C", "#2ECC71", "#9B59B6", "#F39C12",
  "#1ABC9C", "#E67E22", "#2980B9", "#C0392B", "#27AE60",
  "#8E44AD", "#D35400", "#16A085", "#F1C40F", "#34495E",
];

const getAvatarColor = (userId: string): string => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
};

interface AssigneeAvatarStackProps {
  assignees: WorkOrderAssignee[];
  maxVisible?: number;
  size?: number;
  teamName?: string;
}

const AssigneeAvatarStack: React.FC<AssigneeAvatarStackProps> = ({
  assignees,
  maxVisible = 3,
  size = 24,
  teamName,
}) => {
  const { translate } = useTranslation("operations");

  // No assignees => unassigned
  if (!assignees || assignees.length === 0) {
    return (
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "11px",
          color: dt.textMuted,
          fontStyle: "italic",
        }}
      >
        {translate("card.unassigned")}
      </Box>
    );
  }

  // Sort: Lead first
  const sorted = [...assignees].sort((a, b) => {
    if (a.role === 2 && b.role !== 2) return -1;
    if (b.role === 2 && a.role !== 2) return 1;
    return 0;
  });

  const visible = sorted.slice(0, maxVisible);
  const overflow = sorted.length - maxVisible;

  // Build tooltip content
  const tooltipContent = sorted
    .map((a) => `${a.userName}${a.role === 2 ? ` (${translate("assignees.lead")})` : ""}`)
    .join("\n");

  const fontSize = Math.max(Math.round(size * 0.42), 8);
  const overlapOffset = -Math.round(size / 3);

  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: "pre-line", fontFamily: dt.font, fontSize: "11px" }}>
          {tooltipContent}
        </Box>
      }
      enterDelay={400}
      placement="top"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          overflow: "hidden",
        }}
      >
        {/* Team badge */}
        {teamName && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              padding: "1px 8px",
              borderRadius: "10px",
              backgroundColor: "rgba(52, 152, 219, 0.08)",
              border: "1px solid rgba(52, 152, 219, 0.15)",
              fontFamily: dt.font,
              fontSize: "10px",
              fontWeight: 600,
              color: "#3498DB",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {teamName}
          </Box>
        )}

        {/* Avatar stack */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {visible.map((assignee, idx) => {
            const isLead = assignee.role === 2;
            const color = getAvatarColor(assignee.userId);

            return (
              <Box
                key={assignee.userId}
                sx={{
                  width: `${size}px`,
                  height: `${size}px`,
                  borderRadius: "50%",
                  backgroundColor: color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: `${fontSize}px`,
                  fontWeight: 700,
                  fontFamily: dt.font,
                  color: "#FFFFFF",
                  flexShrink: 0,
                  marginLeft: idx === 0 ? 0 : `${overlapOffset}px`,
                  zIndex: visible.length - idx,
                  border: isLead
                    ? "2px solid #F0C05A"
                    : "2px solid #FFFFFF",
                  boxSizing: "border-box",
                }}
              >
                {getInitials(assignee.userName)}
              </Box>
            );
          })}

          {/* Overflow counter */}
          {overflow > 0 && (
            <Box
              sx={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: "50%",
                backgroundColor: dt.cardBorder,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: `${fontSize}px`,
                fontWeight: 700,
                fontFamily: dt.fontMono,
                color: dt.textSecondary,
                flexShrink: 0,
                marginLeft: `${overlapOffset}px`,
                zIndex: 0,
                border: "2px solid #FFFFFF",
                boxSizing: "border-box",
              }}
            >
              +{overflow}
            </Box>
          )}
        </Box>

        {/* Single assignee: show name — Sora 11px/500 #4A4F57 (spec: text-body) */}
        {assignees.length === 1 && (
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              fontWeight: 500,
              color: "#4A4F57",   /* spec: text-body */
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100px",
              lineHeight: "14px",
            }}
          >
            {assignees[0].userName}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export { getAvatarColor, getInitials, AVATAR_COLORS };
export default AssigneeAvatarStack;
